import { type Channel, type Chat, Message } from '@pubnub/chat';
import { useEffect } from 'react';
import { useConversationsStore } from '@avanta/shared/stores/chat/conversations';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import { toSent } from '@avanta/shared/utils/chat';

interface UseRealtimeMessagesParams {
  channelId: string | null;
  chat: Chat | null;
}

export const useRealtimeMessages = ({ channelId, chat }: UseRealtimeMessagesParams): void => {
  useEffect(() => {
    if (!channelId || !chat) return;

    let cancelled = false;
    const unsubscribers: (() => void)[] = [];
    let unsubStreamUpdates: (() => void) | null = null;

    const subscribeToMessageUpdates = (sdkMessages: Message[]): void => {
      unsubStreamUpdates?.();
      unsubStreamUpdates = null;
      if (sdkMessages.length === 0 || cancelled) return;

      unsubStreamUpdates = Message.streamUpdatesOn(sdkMessages, (updated: Message[]) => {
        if (cancelled) return;
        const { upsertMessage } = useMessagesStore.getState();
        updated.forEach(msg => upsertMessage(channelId, toSent(msg)));
      });
    };

    const setup = async (): Promise<void> => {
      const channel = await chat.getChannel(channelId);
      if (!channel || cancelled) return;

      // Incoming real-time messages
      const unsub1 = channel.connect((message: Message) => {
        if (cancelled) return;
        useMessagesStore.getState().upsertMessage(channelId, toSent(message));

        if (message.userId !== chat.currentUser.id) {
          useConversationsStore
            .getState()
            .updateUnreadCount(channelId, 0, String(message.timetoken));
        }
      });
      unsubscribers.push(unsub1);

      // Pinned message changes.
      // streamUpdates receives the UPDATED Channel — use it instead of the
      // stale `channel` reference which may have cached custom data.
      const unsub3 = channel.streamUpdates(async (updatedChannel: Channel) => {
        if (cancelled) return;
        const pinned = await updatedChannel.getPinnedMessage();
        useMessagesStore.getState().setPinnedMessage(channelId, pinned ? toSent(pinned) : null);
      });
      unsubscribers.push(unsub3);

      // Initial subscription to updates for existing messages
      const initialMessages = (useMessagesStore.getState().messages.get(channelId) ?? []).filter(
        msg => !msg.tempId
      ) as Message[];
      subscribeToMessageUpdates(initialMessages);

      // Re-subscribe when new messages arrive (to cover their edits/reactions).
      // Only re-subscribe if the count of real messages changes (added or removed),
      // not on content updates (already handled by the existing subscription).
      // Caching the previous array reference allows an O(1) early-exit when the
      // channel's message list hasn't changed (e.g. store updates from read receipts).
      let prevRealCount = initialMessages.length;
      let prevMsgs = useMessagesStore.getState().messages.get(channelId);
      const unsubStore = useMessagesStore.subscribe(state => {
        const msgs = state.messages.get(channelId);
        if (msgs === prevMsgs) return;
        prevMsgs = msgs;
        const realMsgs = (msgs ?? []).filter(msg => !msg.tempId);
        if (realMsgs.length !== prevRealCount && !cancelled) {
          prevRealCount = realMsgs.length;
          subscribeToMessageUpdates(realMsgs as Message[]);
        }
      });
      unsubscribers.push(unsubStore);
    };

    setup();

    return (): void => {
      cancelled = true;
      unsubStreamUpdates?.();
      unsubscribers.forEach(unsub => unsub());
    };
  }, [channelId, chat]);
};
